import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bookmark, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

export type HeroFilters = Record<string, any>;

interface Props {
  currentFilters: HeroFilters;
  onLoad: (filters: HeroFilters) => void;
}

export function HeroSavedSearches({ currentFilters, onLoad }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const { data: saved = [] } = useQuery({
    queryKey: ['hero-saved-searches', user?.id],
    enabled: !!user && open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('saved_searches')
        .select('id, name, filters, created_at')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const handleSave = async () => {
    if (!user) {
      toast.error('Sign in to save searches');
      return;
    }
    if (!name.trim()) {
      toast.error('Enter a name');
      return;
    }
    setSaving(true);
    const { error } = await supabase.from('saved_searches').insert({
      user_id: user.id,
      name: name.trim(),
      filters: currentFilters,
    });
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success('Search saved');
    setName('');
    qc.invalidateQueries({ queryKey: ['hero-saved-searches', user.id] });
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('saved_searches').delete().eq('id', id);
    if (error) return toast.error(error.message);
    toast.success('Deleted');
    qc.invalidateQueries({ queryKey: ['hero-saved-searches', user?.id] });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
        >
          <Bookmark className="h-3.5 w-3.5" />
          Saved Searches
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[min(20rem,calc(100vw-1.5rem))] p-3"
      >
        {!user ? (
          <p className="text-xs text-muted-foreground">Sign in to save and load searches.</p>
        ) : (
          <>
            <div className="flex gap-2 mb-3">
              <Input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Name this search"
                className="h-9 text-xs"
              />
              <Button size="sm" onClick={handleSave} disabled={saving} className="h-9 shrink-0">
                <Save className="h-3.5 w-3.5 mr-1" /> Save
              </Button>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {saved.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-3">No saved searches yet.</p>
              ) : (
                <ul className="space-y-1">
                  {saved.map((s: any) => (
                    <li key={s.id} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-accent">
                      <button
                        className="flex-1 text-left text-xs truncate"
                        onClick={() => {
                          onLoad(s.filters || {});
                          setOpen(false);
                          toast.success(`Loaded "${s.name}"`);
                        }}
                      >
                        {s.name}
                      </button>
                      <button
                        onClick={() => handleDelete(s.id)}
                        className="text-muted-foreground hover:text-destructive shrink-0"
                        aria-label="Delete saved search"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
